<template>
  <div class="container-fluid pt-0 mt-2">
    <loading :active.sync="isLoading" :is-full-page="fullPage" loader="bars" />

    <div class="card">
      <div class="card-body">
        <!-- Header + toolbar -->
        <div class="d-flex align-items-center justify-content-between mb-3 flex-wrap" style="gap: .8rem">
          <h6 class="font-16 font-weight-bold mb-0">{{ $t('all_triggered_alarms') }}</h6>
          <div class="d-flex align-items-center" style="gap: .6rem">
            <div style="min-width: 16rem">
              <DateComponent :noDefault="true" :resetSignal="dateResetSignal" @period="onPeriodChange" />
            </div>
            <button
              v-if="dateRange.startDate || dateRange.endDate"
              class="btn btn-link p-0 mr-2 clear-btn"
              @click="clearDateRange"
            >
              {{ $t('clear') }}
            </button>
            <button
              class="btn btn-secondary download-btn"
              :disabled="!hasRows"
              @click="exportCsv"
            >
              {{ $t('download') }}
            </button>
            <!-- Search disabled: backend has no search param -->
            <!-- <TableSearch @handleSearch="handleSearch" /> -->
          </div>
        </div>

        <!-- Table (tableMixinWithBackend pattern via child component) -->
        <TriggeredAlarmsTable
          :stats="currentPageRows"
          :paginationMeta="paginationMeta"
          @pageChange="handleTableChange"
          @doSearch="handleTableSearch"
          @edit="onEdit"
          @view="onView"
        />

        <!-- Resolution Modal -->
        <ResolutionModal
          :show="showResolution"
          :alarm="selectedAlarm"
          :readOnly="resolutionViewOnly"
          :initialNote="selectedAlarm && selectedAlarm.note ? selectedAlarm.note : ''"
          @close="showResolution = false"
          @submit="handleResolutionSubmit"
        />

        <!-- Pagination handled by table component through emitted events -->
      </div>
    </div>
  </div>
  
</template>

<script>
import Loading from 'vue-loading-overlay';
import 'vue-loading-overlay/dist/vue-loading.css';
import DateComponent from '@/components/DateComponent.vue';
import ResolutionModal from '@/views/Modals/TriggeredAlarmResolution.vue';
import TriggeredAlarmsTable from '@/components/TriggeredAlarmsTable.vue';
import { mapActions, mapGetters } from 'vuex';
import Vue from 'vue';

export default {
  name: 'TriggeredAlarms',
  components: { Loading, DateComponent, ResolutionModal, TriggeredAlarmsTable },
  data() {
    return {
      isLoading: false,
      fullPage: true,
      pageNumber: 1,
      sizeperpage: 10,
      maxVisibleButtons: 3,
      searchQuery: '',
      dateRange: { startDate: null, endDate: null },
      dateResetSignal: 0,
      showResolution: false,
      selectedAlarm: null,
      resolutionViewOnly: false,
      rows: [],
    };
  },
  computed: {
    ...mapGetters(['alarmTriggers', 'alarmTriggersMeta']),
    mappedRows() {
      // Map API shape to table rows
      const toTitle = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : '');
      return (this.alarmTriggers || []).map((item) => {
        // Detect primary sensor that triggered
        const ex = item?.triggerDetails?.excursionDetails || {};
        const sensorKey = Object.keys(ex).find((k) => ex[k]?.excursion) || 'temperature';
        const range = ex[sensorKey]?.range || {};
        // Readings and units
        const readings = item?.triggerDetails?.sensorReadings || {};
        const value = readings[sensorKey];
        const unit = sensorKey === 'humidity' ? '%' : '°C';

        // Build resolvedBy display name
        const rb = item?.resolution?.resolvedBy || {};
        const resolvedByName = rb.firstName || rb.lastName
          ? [rb.firstName, rb.lastName].filter(Boolean).join(' ')
          : (rb.fullname || rb.name || rb.email || '-');

        return {
          id: item.id,
          alarmId: item?.alarm?._id || item?.alarm?.id || '',
          deviceName: item?.device?.settings?.name || item?.triggerDetails?.deviceName || '-',
          imei: item?.device?.imei || '-',
          sensor: toTitle(sensorKey),
          currentValue: value != null ? `${value}${unit}` : '-',
          expectedRange: (range.min != null && range.max != null) ? `${range.min}${unit} - ${range.max}${unit}` : '-',
          user: resolvedByName,
          status: item?.status === 'active' ? 'critical' : 'stable',
          note: item?.resolution?.note || '',
          timestamp: item?.triggeredAt || item?.createdAt,
        };
      });
    },
    filteredRows() {
      const q = this.searchQuery.trim().toLowerCase();
      return this.mappedRows
        .filter((r) => {
          // Date range filter
          if (this.dateRange.startDate && this.dateRange.endDate) {
            const t = new Date(r.timestamp).getTime();
            const s = new Date(this.dateRange.startDate).getTime();
            const e = new Date(this.dateRange.endDate).getTime();
            if (t < s || t > e) return false;
          }
          // Search filter
          if (!q) return true;
          return [
            r.alarmId,
            r.deviceName,
            r.imei,
            r.sensor,
            r.user || '',
            r.currentValue,
          ]
            .join(' ')
            .toLowerCase()
            .includes(q);
        })
        .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
    },
    currentPageRows() {
      // With server pagination, table receives the current page from API.
      // Apply client search/date filtering only to the current page for now.
      return this.filteredRows;
    },
    paginationMeta() {
      // Use meta from server if available; fallback to computed
      return this.alarmTriggersMeta && Object.keys(this.alarmTriggersMeta).length
        ? this.alarmTriggersMeta
        : { currentPage: this.pageNumber, totalPages: 1, totalItems: this.filteredRows.length };
    },
    hasRows() {
      return Array.isArray(this.currentPageRows) && this.currentPageRows.length > 0;
    },
  },
  methods: {
    ...mapActions(['getAlarmTriggers', 'resolveAlarmTrigger']),
    // Search disabled – backend has no search param
    // handleSearch({ target: { value } }) {
    //   this.searchQuery = value;
    //   this.pageNumber = 1;
    // },
    onPeriodChange({ startdate, enddate }) {
      // Normalize to ISO strings and guard start <= end
      const s = startdate ? new Date(startdate) : null;
      const e = enddate ? new Date(enddate) : null;
      let startISO = s ? s.toISOString() : undefined;
      let endISO = e ? e.toISOString() : undefined;
      if (startISO && endISO && new Date(startISO) > new Date(endISO)) {
        endISO = startISO;
      }
      this.dateRange = { startDate: startISO, endDate: endISO };
      this.pageNumber = 1;
      this.fetchTriggers();
    },
    handleTableChange({ page, limit }) {
      if (limit) this.sizeperpage = +limit;
      if (page) this.pageNumber = +page;
      this.fetchTriggers();
    },
    // Search disabled – backend has no search param
    handleTableSearch(param) {
      if (param?.limit) this.sizeperpage = +param.limit;
      this.pageNumber = 1;
    },
    formatDate(date) {
      const d = new Date(date);
      const pad = (n) => `${n}`.padStart(2, '0');
      const day = pad(d.getDate());
      const month = pad(d.getMonth() + 1);
      const year = `${d.getFullYear()}`.slice(-2);
      let hours = d.getHours();
      const minutes = pad(d.getMinutes());
      const ampm = hours >= 12 ? 'PM' : 'AM';
      hours = hours % 12 || 12;
      return `${day}/${month}/${year} ${pad(hours)}:${minutes}${ampm}`;
    },
    exportCsv() {
      const headers = [
        'Timestamp','Alarm ID','Device Name','Device IMEI','Sensor','Current Value','Expected Range','User','Status','Comment'
      ];
      const rows = this.filteredRows.map((r) => [
        this.formatDate(r.timestamp), r.alarmId, r.deviceName, r.imei, r.sensor,
        r.currentValue, r.expectedRange, r.user || '-', r.status, r.note || ''
      ]);
      const csv = [headers, ...rows].map((row) => row.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(',')).join('\n');
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `triggered_alarms_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    },
    clearDateRange() {
      this.dateRange = { startDate: undefined, endDate: undefined };
      this.dateResetSignal++;
      this.pageNumber = 1;
      this.fetchTriggers();
    },
    handleResolutionSubmit(payload) {
      const id = this.selectedAlarm?.id;
      const note = payload?.note || '';
      if (!id) return;
      this.isLoading = true;
      this.resolveAlarmTrigger({ id, note })
        .then(() => {
          // Optimistic UI: flip to view mode and set note
          this.resolutionViewOnly = true;
          this.selectedAlarm = { ...this.selectedAlarm, status: 'stable', note };
          this.showResolution = false;
          // Refresh current list to stay in sync with server
          this.fetchTriggers();
          try {
            if (this.$toasted) this.$toasted.success(this.$t('alarm_resolved_success'));
            else Vue.toasted && Vue.toasted.success(this.$t('alarm_resolved_success'));
          } catch (e) {}
        })
        .catch(() => {
          try {
            if (this.$toasted) this.$toasted.error(this.$t('alarm_resolved_error'));
            else Vue.toasted && Vue.toasted.error(this.$t('alarm_resolved_error'));
          } catch (e) {}
        })
        .finally(() => (this.isLoading = false));
    },
    onEdit(row) {
      this.selectedAlarm = row;
      this.showResolution = true;
      this.resolutionViewOnly = false;
    },
    onView(row) {
      this.selectedAlarm = row;
      this.resolutionViewOnly = true;
      this.showResolution = true;
    },
    fetchTriggers() {
      // Build params based on current UI state
      const params = {
        page: this.pageNumber,
        limit: this.sizeperpage,
      };
      if (this.dateRange.startDate) params.startDate = this.dateRange.startDate;
      if (this.dateRange.endDate) params.endDate = this.dateRange.endDate;
      this.isLoading = true;
      this.getAlarmTriggers(params)
        .then(() => {
          // Copy store list to local rows mapping source if needed
          this.rows = this.mappedRows;
        })
        .finally(() => (this.isLoading = false));
    },
  },
  mounted() {
    this.fetchTriggers();
  },
};
</script>

<style scoped>
.status-dot {
  display: inline-block;
  width: 10px;
  height: 10px;
  border-radius: 50%;
}
.download-btn {
  border: 0.5px solid #b7c3cd;
  background: white;
  color: #404a57; /* almost black */
}
.clear-btn { color: #404a57; }

.download-btn:hover {
  background: #f6f8fa;
  border-color: #aeb9c3; /* subtle within grey scheme */
  color: #2f3640; /* slightly darker on hover */
}
.download-btn:disabled,
.download-btn[disabled] {
  opacity: 0.6;
  cursor: not-allowed;
  background: #fff;
  color: #a0a6ad; /* softened grey */
  border-color: #c7d0d8;
}
.clear-btn:hover {
  color: #2f3640;
  text-decoration: underline;
  text-underline-offset: 2px;
}
</style>

<i18n>
{
  "en": {
    "all_triggered_alarms": "All Triggered Alarms",
    "download": "Download",
    "clear": "Clear",
    "no_data_found": "No data found",
    "timestamp": "Timestamp",
    "alarm_id": "Alarm ID",
    "device_name": "Device Name",
    "device_imei": "Device IMEI",
    "sensor": "Sensor",
    "current_value": "Current Value",
    "expected_range": "Expected Range",
    "user": "User",
    "status": "Status",
    "alarm_resolved_success": "Alarm resolved successfully",
    "alarm_resolved_error": "Unable to resolve alarm"
  },
  "fr": {
    "all_triggered_alarms": "Toutes les alarmes déclenchées",
    "download": "Télécharger",
    "clear": "Effacer",
    "no_data_found": "Aucune donnée trouvée",
    "timestamp": "Horodatage",
    "alarm_id": "ID d'alarme",
    "device_name": "Nom de l'appareil",
    "device_imei": "IMEI de l'appareil",
    "sensor": "Capteur",
    "current_value": "Valeur actuelle",
    "expected_range": "Plage attendue",
    "user": "Utilisateur",
    "status": "Statut",
    "alarm_resolved_success": "Alarme résolue avec succès",
    "alarm_resolved_error": "Impossible de résoudre l'alarme"
  }
}
</i18n>
